import { useState } from 'react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import Iconify from './Iconify';
import { INavConfig } from 'layouts/NavConfig';

// ----------------------------------------------------------------------

const ListItemStyle: any = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 46,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: 10,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  marginBottom: theme.spacing(0.35),
  transition: 'all 0.18s ease',
  '& .nav-icon': {
    color: alpha(theme.palette.primary.main, 0.5),
    transition: 'color 0.18s',
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.07),
    color: theme.palette.primary.main,
    '& .nav-icon': { color: theme.palette.primary.main },
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 0,
  minWidth: 44,
  transition: 'color 0.18s',
});

// ----------------------------------------------------------------------

interface INavItem {
  item: any;
  active: any;
}

function NavItem({ item, active }: INavItem) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => setOpen((prev: boolean) => !prev);

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 700,
    bgcolor: alpha(theme.palette.primary.main, 0.1),
    '& .nav-icon': { color: theme.palette.primary.main },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '4px',
      height: '55%',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '0 6px 6px 0',          // ← rounder pill indicator
    },
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 700,
  };

  if (children) {
    return (
      <>
        <ListItemStyle onClick={handleOpen} sx={{ ...(isActiveRoot && activeRootStyle) }}>
          <ListItemIconStyle className="nav-icon">{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item: any) => {
              const { title, path } = item;
              const isActiveSub = active(path);
              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{ ...(isActiveSub && activeSubStyle) }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4, height: 4,
                        display: 'flex', borderRadius: '50%',
                        alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && { transform: 'scale(2)', bgcolor: 'primary.main' }),
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle component={RouterLink} to={path} sx={{ ...(isActiveRoot && activeRootStyle) }}>
      <ListItemIconStyle className="nav-icon">{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}

export function NavSection({ navConfig, ...other }: { navConfig: INavConfig[] }) {
  const { pathname } = useLocation();
  const match = (path: string) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1.25 }}>
        {navConfig?.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
